import React from 'react';
import { Home, Receipt, Report } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { alpha } from '@mui/material/styles';
import { useCanViewOrders, useCanViewOutOfStock } from '../../../../shared/utils/permissions';
import useRestaurantStore from '../../../../stores/restaurantStore';
import NavItem, { NavList } from './NavItem';
import OutOfStockBadge from './OutOfStockBadge';
import RestaurantSelect from './RestaurantSelect';
import { isOverviewPage } from './useSelectedShop';

type RestaurantNavProps = {
  selectedShopId?: string;
  allowedShops: string[];
  onNavigate: () => void;
};

const RestaurantNav: React.FC<RestaurantNavProps> = ({ selectedShopId, allowedShops, onNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canViewOrders = useCanViewOrders();
  const canViewOutOfStock = useCanViewOutOfStock();
  const { pathname, search } = useLocation();
  const selectRestaurant = useRestaurantStore((state) => state.selectRestaurant);
  const showPicker = allowedShops.length > 1;
  const isOverview = isOverviewPage(pathname);
  // On the overview no shop is chosen, so the shop links below have nothing honest to point at: they
  // stay in place, dimmed, under a line saying what to do. One-shop users have no picker and their `/`
  // is the dashboard, so this never applies to them.
  const linksDisabled = showPicker && isOverview;

  // On a restaurant's own page, open the same page for the new shop (`/0171/orders` → `/9999/orders`). From
  // the overview, open the shop's dashboard: staying on the grid with the picker naming one shop would read
  // as nothing having happened. Anywhere else, only remember the choice; the links below follow it.
  const handleSelect = (shopId: string) => {
    const [, firstSegment] = pathname.split('/');
    if (selectedShopId && firstSegment === selectedShopId) {
      navigate(`/${shopId}${pathname.slice(firstSegment.length + 1)}${search}`);
    } else if (isOverview) {
      navigate(`/${shopId}/home`);
    } else {
      selectRestaurant(shopId);
    }
    onNavigate();
  };

  const handleSelectAll = () => {
    navigate('/');
    onNavigate();
  };

  return (
    <>
      {showPicker && (
        <PickerSlot>
          <RestaurantSelect
            allowedShops={allowedShops}
            selectedShopId={selectedShopId}
            isOverviewSelected={isOverview}
            onSelect={handleSelect}
            onSelectAll={handleSelectAll}
          />
          {linksDisabled && <PickerHint>{t('nav.restaurant.selectRestaurant')}</PickerHint>}
        </PickerSlot>
      )}
      {selectedShopId && (
        // The hint takes its height off the list's offset, so the items stay put when it appears.
        <NavList $offset={showPicker ? (linksDisabled ? 38 - HINT_HEIGHT : 38) : 32}>
          <NavItem
            to={`/${selectedShopId}/home`}
            icon={<Home />}
            label={t('nav.restaurant.start')}
            disabled={linksDisabled}
            onNavigate={onNavigate}
          />
          {/* These checks must match the route guards, or the links hide from users who may open the pages. */}
          {canViewOrders && (
            <NavItem
              to={`/${selectedShopId}/orders/`}
              icon={<Receipt />}
              label={t('nav.restaurant.orders')}
              disabled={linksDisabled}
              onNavigate={onNavigate}
            />
          )}
          {canViewOutOfStock && (
            <NavItem
              to={`/${selectedShopId}/out-of-stock/`}
              // No badge while no shop is chosen: a count would be some other shop's.
              icon={
                linksDisabled ? (
                  <Report />
                ) : (
                  <OutOfStockBadge shopId={selectedShopId}>
                    <Report />
                  </OutOfStockBadge>
                )
              }
              label={t('nav.restaurant.outOfStock')}
              disabled={linksDisabled}
              onNavigate={onNavigate}
            />
          )}
        </NavList>
      )}
    </>
  );
};

// 22px from the drawer's edges rather than the nav's 48px.
const PickerSlot = styled.div`
  margin: 30px -26px 0;
`;

// The hint's margin plus its line, which the list beneath gives back.
const HINT_MARGIN = 8;
const HINT_LINE_HEIGHT = 16;
const HINT_HEIGHT = HINT_MARGIN + HINT_LINE_HEIGHT;

// A quiet line under the picker, in the drawer's secondary white (the admin tab's section headers use the
// same): the links below are off until a shop is picked. Indented back to the nav's gutter.
const PickerHint = styled.div`
  margin: ${HINT_MARGIN}px 26px 0;
  color: ${({ theme }) => alpha(theme.palette.common.white, 0.7)};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  line-height: ${HINT_LINE_HEIGHT}px;
`;

export default RestaurantNav;
